import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../../msalConfig';
import { getItems, FAMILY_LISTS } from '../../api/figApi';
import MediaCover from '../components/MediaCover';

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} ${d.getUTCFullYear()}`;
}

export default function Videos() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const [items, setItems]           = useState(null); // null = loading
  const [error, setError]           = useState(null);
  const [sortBy, setSortBy]         = useState('event');
  const [filterAlbum, setFilterAlbum]   = useState('all');
  const [filterPerson, setFilterPerson] = useState('all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const all = await getItems(token, FAMILY_LISTS.MEDIA_ITEMS);
        if (!cancelled) {
          setItems(all.filter(item => item.MediaType === 'Video'));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  // Distinct AlbumCollection values
  const albumOptions = items
    ? [...new Set(items.map(i => i.AlbumCollection).filter(Boolean))].sort()
    : [];

  // Distinct people extracted from comma-separated People strings
  const peopleOptions = items
    ? [...new Set(
        items.flatMap(i =>
          (i.People ?? '').split(',').map(s => s.trim()).filter(Boolean)
        )
      )].sort()
    : [];

  // Build display list: both filters (AND), then sort
  let displayed = items ?? [];
  if (filterAlbum !== 'all') {
    displayed = displayed.filter(i => i.AlbumCollection === filterAlbum);
  }
  if (filterPerson !== 'all') {
    const target = filterPerson.toLowerCase();
    displayed = displayed.filter(i => {
      const names = (i.People ?? '').split(',').map(s => s.trim().toLowerCase());
      return names.includes(target);
    });
  }
  displayed = [...displayed].sort((a, b) => {
    switch (sortBy) {
      case 'event': {
        const ad = a.DateOfEvent ?? '';
        const bd = b.DateOfEvent ?? '';
        if (!ad && bd) return 1;
        if (ad && !bd) return -1;
        return bd.localeCompare(ad); // descending — newest event first
      }
      case 'recent':
        return (b.Created ?? '').localeCompare(a.Created ?? '');
      case 'title':
        return (a.Title ?? '').toLowerCase().localeCompare((b.Title ?? '').toLowerCase());
      default:
        return 0;
    }
  });

  const anyFilterActive = filterAlbum !== 'all' || filterPerson !== 'all';

  return (
    <div className="FV-shelf">
      <h1 className="FV-shelf-title">Videos</h1>

      <div className="fv-browse-toolbar">
        <select
          className="fv-browse-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort by"
        >
          <option value="event">Date of Event</option>
          <option value="recent">Recently Added</option>
          <option value="title">Title</option>
        </select>

        <select
          className="fv-browse-select"
          value={filterAlbum}
          onChange={e => setFilterAlbum(e.target.value)}
          aria-label="Filter by album"
        >
          <option value="all">All Albums</option>
          {albumOptions.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        <select
          className="fv-browse-select"
          value={filterPerson}
          onChange={e => setFilterPerson(e.target.value)}
          aria-label="Filter by person"
        >
          <option value="all">All People</option>
          {peopleOptions.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {items === null && !error && (
        <p className="FV-empty">Loading…</p>
      )}

      {error && (
        <p className="fv-browse-error">{error}</p>
      )}

      {items !== null && !error && displayed.length === 0 && (
        <p className="fv-browse-empty">
          {anyFilterActive ? 'No videos match those filters.' : 'No videos yet.'}
        </p>
      )}

      {displayed.length > 0 && (
        <div className="fv-browse-grid fv-browse-grid--wide">
          {displayed.map(item => {
            const secondary = item.AlbumCollection || formatEventDate(item.DateOfEvent);
            return (
              <div
                key={item.id}
                className="fv-browse-card"
                onClick={() => navigate(`/videos/${item.id}`)}
              >
                <MediaCover item={item} aspectRatio="16:9" size="lg" />
                <span className="fv-browse-card-title">{item.Title}</span>
                {secondary && (
                  <span className="fv-browse-card-secondary">{secondary}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
